import { t } from "@lingui/macro"
import React from "react"
import { Box, Typography, Stack } from "@mui/material"
import { observer } from "mobx-react"

import { adminv3_AsyncRequestStatusSummaryResponse } from "src/api/models/adminv3_AsyncRequestStatusSummaryResponse"
import { avy_api_pkg_async_AsyncStatus } from "src/api/models/avy_api_pkg_async_AsyncStatus"
import { IDateRangeValue } from "src/types/date_range_picker"

interface IAsyncRequestStatusSummaryProps {
    statusSummary: adminv3_AsyncRequestStatusSummaryResponse[]
    dateRangeFilter: IDateRangeValue
    onDateRangeChange: (value: IDateRangeValue) => void
    getStatusColor: (
        status: avy_api_pkg_async_AsyncStatus,
    ) =>
        | "default"
        | "primary"
        | "secondary"
        | "error"
        | "info"
        | "success"
        | "warning"
    getStatusText: (status: avy_api_pkg_async_AsyncStatus) => string
    onProcessingClick?: () => void
}

export const AsyncRequestStatusSummary = observer(
    ({
        statusSummary,
        dateRangeFilter: _dateRangeFilter,
        onDateRangeChange: _onDateRangeChange,
        getStatusColor: _getStatusColor,
        getStatusText,
        onProcessingClick,
    }: IAsyncRequestStatusSummaryProps) => {
        const processingItem = statusSummary.find(
            (item) => item.status === "processing",
        )
        const processingCount = processingItem?.count ?? 0

        return (
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: "background.paper",
                    borderRadius: 1,
                }}
            >
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t`async-requests.summary.title`}
                        </Typography>
                    </Box>

                    {/* Processing Count Button */}
                    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                        <Box
                            onClick={onProcessingClick}
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 2,
                                py: 1.5,
                                px: 3,
                                backgroundColor: "grey.100",
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "grey.300",
                                minWidth: "120px",
                                cursor:
                                    onProcessingClick != null
                                        ? "pointer"
                                        : "default",
                                transition: "all 0.2s ease-in-out",
                                "&:hover":
                                    onProcessingClick != null
                                        ? {
                                              backgroundColor: "grey.200",
                                              borderColor: "grey.400",
                                              transform: "translateY(-1px)",
                                              boxShadow: 1,
                                          }
                                        : {},
                            }}
                        >
                            <Typography
                                variant="h5"
                                component="span"
                                sx={{
                                    fontWeight: "bold",
                                    color: "text.primary",
                                }}
                            >
                                {processingCount}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "text.secondary",
                                    fontWeight: "medium",
                                }}
                            >
                                {getStatusText("processing")}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Box>
        )
    },
)
